import { Button } from "@/components/ui/button";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

import { Label } from "@/components/ui/label";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { useDebounce } from "@/hooks/useDebounce";
import { Neighborhood, useNeighborhoods } from "@/hooks/useNeighborhoods";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, MapPin } from "lucide-react";
import { useState } from "react";

interface PropertyNeighborhoodSelectorProps {
  selectedNeighborhood: Neighborhood | null;
  onNeighborhoodSelect: (neighborhood: Neighborhood | null) => void;
  canEdit?: boolean;
}

const PropertyNeighborhoodSelector = ({
  selectedNeighborhood,
  onNeighborhoodSelect,
  canEdit = false,
}: PropertyNeighborhoodSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { data: neighborhoods = [], isLoading } =
    useNeighborhoods(debouncedSearchTerm);

  return (
    <div>
      <Label>Ciudad</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={!canEdit}
            className="w-full justify-between"
          >
            {selectedNeighborhood ? (
              <div className="flex items-center">
                <MapPin className="mr-2 h-4 w-4" />
                {selectedNeighborhood.city}, {selectedNeighborhood.state}
              </div>
            ) : (
              "Seleccionar ciudad..."
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-full p-0">
          <Command>
            <CommandInput
              placeholder="Buscar ciudad..."
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
            <CommandList>
              {isLoading ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  Cargando ciudades...
                </div>
              ) : (
                <>
                  <CommandEmpty>No se encontraron ciudades.</CommandEmpty>
                  <CommandGroup>
                    {neighborhoods.map((neighborhood) => (
                      <CommandItem
                        key={neighborhood.id}
                        value={`${neighborhood.city} ${neighborhood.state}`}
                        onSelect={() => {
                          onNeighborhoodSelect(
                            selectedNeighborhood?.id === neighborhood.id
                              ? null
                              : neighborhood,
                          );
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedNeighborhood?.id === neighborhood.id
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        <div className="flex items-center">
                          <MapPin className="mr-2 h-4 w-4" />
                          <div>
                            <div className="font-medium">
                              {neighborhood.city}, {neighborhood.state}
                            </div>
                            <div className="text-xs text-gray-500">
                              {neighborhood.country}
                            </div>
                            {neighborhood.description && (
                              <div className="text-xs text-gray-400 mt-1">
                                {neighborhood.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default PropertyNeighborhoodSelector;
